import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { envVariables } from '@fabric-ui/utils';
import * as Sentry from '@sentry/react';
import type { ErrorEvent as SentryErrorEvent, Event as SentryEvent, EventHint as SentryEventHint } from '@sentry/react';
import { CodeOwner } from '@utils/router.utils';
import kfuseRumSDK from 'kf-browser-sdk';
import { type AvRouter } from '../RouterInit';
import { getExporterWorkflowMetadata, isExportMode } from './exportDashboards.utils';

const isSynthetics =
  window._DATADOG_SYNTHETICS_BROWSER || // eslint-disable-line no-underscore-dangle
  navigator.userAgent.includes('Datadog') ||
  navigator.userAgent.includes('checklyhq.com');

const isDebugMode = () => {
  const resetDebugModeCount = Number(localStorage.getItem('resetDebugModeCount'));
  if (!['prod-se01', 'prod'].includes(envVariables.VITE_ENV) || resetDebugModeCount > 0) {
    return localStorage.getItem('debugMode') === 'true';
  }
  localStorage.setItem('debugMode', 'false');
  localStorage.setItem('resetDebugModeCount', `${resetDebugModeCount + 1}`);
  return false;
};

const shouldRecordSession = !isDebugMode() && !isSynthetics && !envVariables.STORYBOOK && envVariables.VITE_DD_CLIENT_TOKEN;

const getBody = (context: any) => {
  try {
    return context.requestInit?.body && typeof context.requestInit.body === 'string' && JSON.parse(context.requestInit.body);
  } catch (e) {
    console.warn(e, context.requestInit?.body);
    return '<body failed to parse>';
  }
};

const getFetchDetails = (context: any) => ({
  request: { graphqlname: context.requestInit?.headers?.graphqlname, body: getBody(context) },
  response: { headers: Object.fromEntries(context.response?.headers || []) },
});

const getOwnerByErrorMessage = ({
  message,
  handling_stack: stackTrace,
}: {
  message: string;
  handling_stack?: string;
}): CodeOwner | undefined => {
  const lowerCaseMessage = message.toLowerCase();
  const isQueryError = ['queryobjectasync', 'queryobject', 'querysql'].some(term => lowerCaseMessage.includes(term));

  if ((lowerCaseMessage.includes('effect failure') || lowerCaseMessage.includes('failed to fetch')) && isQueryError) return CodeOwner.Query;
  if (lowerCaseMessage.includes('[authentication error]')) return CodeOwner.Authentication;
  if (/\bpendo\b/i.test(stackTrace ?? '') || /\bpendo\b/.test(lowerCaseMessage)) return CodeOwner.Pendo;
  if (lowerCaseMessage.includes('[debugging enabled]')) return CodeOwner.Debugging;
  if (lowerCaseMessage.includes('uncaught "empty reason"') || lowerCaseMessage.includes('[context error]')) return CodeOwner.Frontend;
  return undefined;
};

const getOwnerFromStaticRoute = ({ matches }): CodeOwner | undefined =>
  matches.findLast(({ staticData }) => staticData?.owner)?.staticData.owner;

const getOriginalErrorMessage = (value: unknown): string | undefined => {
  if (value instanceof Error) {
    const { response } = value as { response?: unknown };
    if (response) {
      const responseMessage = getOriginalErrorMessage(response);
      if (responseMessage) return responseMessage;
    }
    return value.message;
  }
  if (Array.isArray(value)) {
    const messages = value.map(getOriginalErrorMessage).filter((msg): msg is string => Boolean(msg));
    return messages.length ? messages.join(', ') : undefined;
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    if (record.error) {
      const errorMessage = getOriginalErrorMessage(record.error);
      if (errorMessage) return errorMessage;
    }
    if (record.errors) {
      const errorsMessage = getOriginalErrorMessage(record.errors);
      if (errorsMessage) return errorsMessage;
    }
    if (record.response) {
      const responseMessage = getOriginalErrorMessage(record.response);
      if (responseMessage) return responseMessage;
    }
    if (typeof record.message === 'string') return record.message;
  }
  return undefined;
};

const getOriginalErrorStatus = (value: unknown): string | undefined => {
  const queue = [value];
  const seen = new WeakSet<object>();

  while (queue.length) {
    const current = queue.shift();
    if (current && typeof current === 'object' && !seen.has(current)) {
      seen.add(current);
      if (Array.isArray(current)) {
        queue.push(...current);
      } else {
        const record = current as Record<string, unknown>;
        const statuses = [record.status, (record.extensions as Record<string, unknown> | undefined)?.status];
        const status = statuses.find(
          candidate => (typeof candidate === 'number' || typeof candidate === 'string') && /^\d{3}$/.test(String(candidate))
        );
        if (status !== undefined) return String(status);
        queue.push(record.error, record.errors, record.response, record.cause);
      }
    }
  }

  return undefined;
};

const resolveEventMessage = (event: SentryEvent, hint?: SentryEventHint): string | undefined => {
  const req = hint?.originalException;
  const wrappedArgs = event.extra?.arguments;
  const firstWrappedArg = Array.isArray(wrappedArgs) ? wrappedArgs[0] : undefined;
  const fromReq = getOriginalErrorMessage(req) ?? getOriginalErrorMessage(firstWrappedArg);
  if (fromReq != null) return fromReq;

  return event.message || event.exception?.values?.at(-1)?.value;
};

const consoleErrorWrappedSymbol = Symbol.for('avRum.consoleError.wrapped');
const consoleErrorMarkSymbol = Symbol.for('avRum.consoleError.mark');

const getWrappedConsoleErrorPayload = (value: unknown): unknown =>
  value instanceof Error && Object.prototype.hasOwnProperty.call(value, consoleErrorMarkSymbol) ? value.cause : undefined;

const getOwnerByConsoleErrorPayload = (value: unknown): CodeOwner | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  const { graphqlName } = value as { graphqlName?: unknown };
  return typeof graphqlName === 'string' && ['queryobjectasync', 'queryobject', 'querysql'].includes(graphqlName.toLowerCase())
    ? CodeOwner.Query
    : undefined;
};

interface ConsoleErrorFunction {
  (this: Console, ...args: unknown[]): void;
  [consoleErrorWrappedSymbol]?: boolean;
  __sentry_original__?: ConsoleErrorFunction;
}

const copyFunctionProperties = (target: ConsoleErrorFunction, source: ConsoleErrorFunction) => {
  [...Object.getOwnPropertyNames(source), ...Object.getOwnPropertySymbols(source)].forEach(key => {
    if (typeof key === 'string' && (key === 'prototype' || key === 'arguments' || key === 'caller')) {
      return;
    }
    const descriptor = Object.getOwnPropertyDescriptor(source, key);
    if (descriptor) {
      try {
        Object.defineProperty(target, key, descriptor);
      } catch {
        // Ignore non-configurable properties.
      }
    }
  });
};

/* eslint-disable no-underscore-dangle -- Sentry stores the original function in __sentry_original__ */
const isConsoleErrorAlreadyWrapped = (fn: ConsoleErrorFunction): boolean => {
  if (fn[consoleErrorWrappedSymbol]) return true;
  const original = fn.__sentry_original__;
  return original ? isConsoleErrorAlreadyWrapped(original) : false;
};
/* eslint-enable no-underscore-dangle */

// Wrap console.error so any non-string, non-Error first argument is forwarded as
// an Error carrying the original call-site stack. Sentry's captureConsoleIntegration
// then captures a readable message (instead of "[object Object]") and a stack trace
// that owner routing can inspect. Strings pass through so native %s/%d formatting
// keeps working.
const wrapConsoleErrorForSentry = () => {
  if (typeof window === 'undefined' || typeof console === 'undefined') return;

  const currentConsoleError = console.error as ConsoleErrorFunction;
  if (isConsoleErrorAlreadyWrapped(currentConsoleError)) return;

  const wrappedConsoleError: ConsoleErrorFunction = function wrappedConsoleError(this: Console, ...args: unknown[]) {
    const first = args[0];
    if (args.length === 0 || first instanceof Error || typeof first === 'string') return currentConsoleError.apply(this, args);
    const message = (() => {
      try {
        const extractedMessage = getOriginalErrorMessage(first);
        if (extractedMessage !== undefined) return extractedMessage;
        if ((first !== null && typeof first === 'object') || typeof first === 'function') return 'console error';
        return String(first);
      } catch {
        return 'console error';
      }
    })();
    const error = ((): Error => {
      try {
        const e = new Error(message, { cause: first });
        if (e.cause !== first) throw new Error('cause not supported');
        return e;
      } catch {
        const e = new Error(message);
        try {
          Object.defineProperty(e, 'cause', { value: first, configurable: true, writable: true });
        } catch {
          // Ignore runtimes where Error.cause is non-configurable.
        }
        return e;
      }
    })();
    Object.defineProperty(error, consoleErrorMarkSymbol, {
      value: true,
      enumerable: false,
      configurable: false,
      writable: false,
    });
    // Forward the normalized Error plus the remaining original arguments only,
    // so the original object is not duplicated in the argument list.
    return currentConsoleError.apply(this, [error, ...args.slice(1)]);
  };

  copyFunctionProperties(wrappedConsoleError, currentConsoleError);
  Object.defineProperty(wrappedConsoleError, consoleErrorWrappedSymbol, {
    value: true,
    enumerable: false,
    configurable: false,
    writable: false,
  });

  console.error = wrappedConsoleError;
};

const createAvRum = () => {
  const state = {
    isInitialized: false,
    isKfuseEnabled: false,
    isSentryEnabled: false,
    router: null as AvRouter | null,
    lastUser: null as Record<string, any> | null,
  };

  const CONTEXT_TAG_KEYS = ['accountName', 'accountType', 'accountId', 'userName', 'userId'];

  const getContextTags = (user: Record<string, any> | null) => {
    const tags = CONTEXT_TAG_KEYS.reduce(
      (result, key) => {
        if (user && (typeof user[key] === 'string' || typeof user[key] === 'number') && user[key]) {
          // eslint-disable-next-line no-param-reassign
          result[key] = String(user[key]);
        }
        return result;
      },
      {} as Record<string, string>
    );
    const userEmail = typeof user?.email === 'string' && user.email ? user.email : undefined;
    return userEmail ? { ...tags, userEmail, impactedUser: userEmail } : tags;
  };

  const getOwner = () => (state.router ? getOwnerFromStaticRoute(state.router.state) : undefined) || CodeOwner.Frontend;

  const setOwnerContext = (owner: CodeOwner) => {
    datadogRum.setGlobalContextProperty('owner', owner);
    if (state.isKfuseEnabled) {
      kfuseRumSDK.setGlobalContext({ owner });
    }
    if (state.isSentryEnabled) {
      Sentry.setTag('owner', owner);
    }
  };

  const init = (routerInstance: AvRouter) => {
    if (!shouldRecordSession) return;

    state.router = routerInstance;
    state.isInitialized = true;

    const commonConfig = {
      service: 'app-avalor',
      env: envVariables.VITE_ENV,
      version: envVariables.VITE_VERSION,
    };

    // Initialize Datadog RUM
    datadogRum.init({
      ...commonConfig,
      applicationId: '79576ecf-b1bc-4bf0-ab5f-a99515bd4b2c',
      clientToken: envVariables.VITE_DD_CLIENT_TOKEN,
      site: 'datadoghq.com',
      sessionReplaySampleRate: 100,
      compressIntakeRequests: true,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'allow',
      plugins: [reactPlugin({ router: false })],
      beforeSend: (event, context: any) => {
        const owner = (event.type === 'error' && getOwnerByErrorMessage(event.error)) || getOwner();
        const fetchDetails = event.type === 'resource' && event.resource.type === 'fetch' ? getFetchDetails(context) : {};

        // eslint-disable-next-line no-param-reassign
        event.context = { ...event.context, owner, ...fetchDetails };
        return true;
      },
    });

    // Initialize Kfuse RUM if configured
    const shouldInitializeKfuse = false;
    if (shouldInitializeKfuse && envVariables.VITE_KFUSE_APPLICATION_ID) {
      state.isKfuseEnabled = true;
      kfuseRumSDK.init({
        config: {
          ...commonConfig,
          applicationId: envVariables.VITE_KFUSE_APPLICATION_ID,
          clientToken: envVariables.VITE_KFUSE_CLIENT,
          proxy: envVariables.VITE_KFUSE_URL,
          sessionSampleRate: 100,
          defaultPrivacyLevel: 'allow',
          enableSessionRecording: true,
          enableLogCollection: true,
        },
      });
    }

    datadogRum.startSessionReplayRecording();

    // Initialize Sentry RUM if configured
    if (envVariables.VITE_SENTRY_DSN) {
      state.isSentryEnabled = true;
      Sentry.init({
        dsn: envVariables.VITE_SENTRY_DSN,
        environment: commonConfig.env,
        release: commonConfig.version,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.captureConsoleIntegration({ levels: ['error'] }),
          Sentry.replayIntegration({
            maskAllText: false,
            maskAllInputs: false,
            blockAllMedia: false,
            // SDK defaults mask title/placeholder/aria-label to "*****" even
            // with maskAllText off — which breaks CSS attribute selectors like
            // `main[aria-label='workspace']` in replays.
            maskAttributes: [],
            networkDetailAllowUrls: [envVariables.VITE_API_URL],
            networkRequestHeaders: ['graphqlname', 'x-request-id', 'traceparent', 'x-b3-traceid'],
            networkResponseHeaders: ['x-request-id', 'traceparent', 'x-b3-traceid'],
          }),
        ],
        tracesSampleRate: 1.0,
        normalizeDepth: 10,
        replaysSessionSampleRate: 1.0,
        replaysOnErrorSampleRate: 1.0,
        beforeSend: (event: SentryErrorEvent, hint?: SentryEventHint): SentryErrorEvent | null => {
          const req = hint?.originalException;
          const wrappedArgs = event.extra?.arguments;
          const firstWrappedArg = Array.isArray(wrappedArgs) ? wrappedArgs[0] : undefined;
          const consoleErrorPayload = getWrappedConsoleErrorPayload(req) ?? getWrappedConsoleErrorPayload(firstWrappedArg);
          const message = resolveEventMessage(event, hint);
          const status =
            getOriginalErrorStatus(req) ?? getOriginalErrorStatus(consoleErrorPayload) ?? getOriginalErrorStatus(firstWrappedArg);
          const searchableMessage = message && status ? `Status ${status}: ${message}` : message;
          const isStructuredRejection = (value?: string) => value?.includes('Object captured as promise rejection with keys:') ?? false;

          // If Sentry stringified the original exception to "[object Object]",
          // replace the event message and exception values with the real message
          // we extracted from the original object/Error.
          if (
            searchableMessage &&
            (status || event.message === '[object Object]' || event.exception?.values?.some(v => v.value === '[object Object]'))
          ) {
            if (status || event.message === '[object Object]') {
              // eslint-disable-next-line no-param-reassign
              event.message = searchableMessage;
            }
            event.exception?.values?.forEach(exceptionValue => {
              if (
                exceptionValue.value === '[object Object]' ||
                (status && (exceptionValue.value === message || isStructuredRejection(exceptionValue.value)))
              ) {
                // eslint-disable-next-line no-param-reassign
                exceptionValue.value = searchableMessage;
              }
            });
          }

          const isWrapperCreatedError =
            (req instanceof Error && Object.prototype.hasOwnProperty.call(req, consoleErrorMarkSymbol)) ||
            (firstWrappedArg instanceof Error && Object.prototype.hasOwnProperty.call(firstWrappedArg, consoleErrorMarkSymbol));
          if (consoleErrorPayload !== undefined) {
            // eslint-disable-next-line no-param-reassign
            event.extra = { ...event.extra, originalConsoleError: consoleErrorPayload };
          }
          const culpritUrl = typeof window !== 'undefined' ? window.location.href : '';
          const isWrappedConsoleError = event.logger === 'console' && culpritUrl && isWrapperCreatedError;
          if (isWrappedConsoleError) {
            // Use the complete browser URL as the culprit so Slack links to the
            // exact application state where the console error occurred.
            Object.assign(event, { culprit: culpritUrl });
          }
          const exceptionStack = event.exception?.values?.[0]?.stacktrace?.frames
            ?.map(frame => frame.abs_path ?? frame.filename)
            .filter((path): path is string => !!path)
            .join('\n');
          const owner =
            getOwnerByConsoleErrorPayload(consoleErrorPayload) ||
            (message && getOwnerByErrorMessage({ message, handling_stack: req instanceof Error ? req.stack : exceptionStack })) ||
            getOwner();
          const viewName = typeof window !== 'undefined' ? window.location.pathname : undefined;
          // eslint-disable-next-line no-param-reassign
          event.tags = {
            ...event.tags,
            ...(owner ? { owner } : {}),
            ...(viewName ? { 'view.name': viewName } : {}),
            ...getContextTags(state.lastUser),
          };
          return event;
        },
      });

      // Wrap console.error after Sentry.init so our wrapper is outermost and
      // Sentry's captureConsole integration receives the normalized Error.
      wrapConsoleErrorForSentry();
    }

    // Set initial owner context for all providers
    setOwnerContext(getOwner());
  };

  const setUser = (userData: Record<string, any>) => {
    if (!state.isInitialized) return;

    state.lastUser = userData;
    const { userName, userId, ...rest } = userData;
    const commonData = { ...rest, ...(userName && { name: userName }) };

    // Set Datadog user
    datadogRum.setUser({ ...commonData, ...(userId && { userId }) });

    // Set Kfuse user (userId maps to id)
    if (state.isKfuseEnabled) {
      kfuseRumSDK.setUser({ ...commonData, ...(userId && { id: userId }) });
    }

    // Set Sentry user (userId maps to id)
    if (state.isSentryEnabled) {
      Sentry.setUser({ ...commonData, ...(userId && { id: String(userId) }) });
    }
  };

  const addAction = (name: string, context?: object) => {
    if (!state.isInitialized) return;
    const owner = getOwner();
    datadogRum.addAction(name, { ...context, owner });
    if (state.isKfuseEnabled) {
      kfuseRumSDK.addAction?.(name, { ...context, owner });
    }
    if (state.isSentryEnabled) {
      Sentry.addBreadcrumb({ category: 'action', message: name, data: { ...context, owner } });
    }
  };

  const addError = (error: unknown, context?: object) => {
    if (!state.isInitialized) return;
    const owner = getOwner();
    datadogRum.addError(error, { ...context, owner });
    if (state.isSentryEnabled) {
      Sentry.captureException(error, { extra: { ...context, owner } });
    }
  };

  return {
    init,
    setUser,
    getUser: () => (state.isInitialized ? datadogRum.getUser() : {}),
    addAction,
    addError,
    setGlobalContextProperty: (key: string, value: any) => {
      if (state.isInitialized) {
        datadogRum.setGlobalContextProperty(key, value);
        if (state.isKfuseEnabled) kfuseRumSDK.setGlobalContext?.({ [key]: value });
        if (state.isSentryEnabled) Sentry.setTag(key, value);
      }
    },
    removeGlobalContextProperty: (key: string) => {
      if (state.isInitialized) {
        datadogRum.removeGlobalContextProperty(key);
        if (state.isSentryEnabled) Sentry.setTag(key, undefined);
      }
    },
    get isRecording() {
      return !!shouldRecordSession;
    },
  };
};

export const avRum = createAvRum();

export { isDebugMode, shouldRecordSession, isSynthetics };

export const initRum = (routerInstance: AvRouter) => {
  avRum.init(routerInstance);

  if (isExportMode() && shouldRecordSession) {
    avRum.setUser({ ...avRum.getUser(), ...getExporterWorkflowMetadata() });
  }
};
